import styled from '@emotion/styled';
import { FocusTrap } from 'focus-trap-react';
import { FC, PropsWithChildren, useEffect, useState } from 'react';

import Container, { ContainerPosition } from './Container';
import Overlay from './Overlay';
import styles from '../../constants/styles';
import BodyEnd from '../BodyEnd';
import Box from '../Box';
import Divider from '../Divider';
import Flex from '../Flex';
import IconButton from '../IconButton';
import Heading3 from '../Text/Heading3';

// Local Typings
export interface DrawerProps {
  isFullScreen?: boolean;
  isOpen?: boolean;
  isPaddingDisabled?: boolean;
  onClose?: () => void;
  onExited?: () => void;
  position: ContainerPosition;
  shouldCloseOnClickAway?: boolean;
  title?: string;
  transition?: number;
  withFocusTrap?: boolean;
  withTint?: boolean;
}

const DrawerContainer = styled(Box)({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  '.drawer_overlay': {
    width: '100vw',
    zIndex: styles.zIndex.drawer,
  },
});

// Component Definition
const Drawer: FC<PropsWithChildren<DrawerProps>> = ({
  isOpen = true,
  onExited,
  transition = 300,
  withFocusTrap = true,
  ...props
}) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShow(false);
    }

    const timeout = setTimeout(() => {
      setShouldRender(isOpen);
      if (!isOpen) {
        onExited?.();
      }
    },
    isOpen ? 0 : transition);

    return () => {
      clearTimeout(timeout);
    };
  },
  [isOpen, onExited, transition]);

  useEffect(() => {
    let timeout: number;

    if (shouldRender) {
      timeout = window.setTimeout(() => {
        setShow(true);
      },
      100);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  },
  [shouldRender]);

  const container = (
    <Container
      isFullScreen={props.isFullScreen}
      isOpen={show}
      isPaddingDisabled={props.isPaddingDisabled}
      position={props.position}
      transition={transition}
    >
      <Flex
        alignSelf="stretch"
        fullWidth
        justifyContent="space-between"
        paddingBottom={!!props.title || !!props.onClose ? 6 : undefined}
      >
        {props.title && (
          <Heading3
            fontSize={20}
            fontWeight="bold"
            lineHeight="1.0em"
          >
            {props.title}
          </Heading3>
        )}

        {props.onClose && (
          <>
            {!props.title && <div />}

            <Flex
              // negative amounts to allow the container to control padding
              marginX={-3}
              marginY={-2}
            >
              <IconButton
                circle
                iconName="close"
                iconSize="xxs"
                onClick={props.onClose}
              />
            </Flex>
          </>
        )}
      </Flex>

      {props.title && (
        <Divider
          dividerWidth="100%"
          marginY={0}
        />
      )}

      <Flex
        flexDirection="column"
        flexGrow={1}
        hideScrollBar
        overflowY="auto"
        paddingTop={props.title ? 4 : undefined}
      >
        {props.children}
      </Flex>
    </Container>
  );

  const content = (
    <Overlay
      className="drawer_overlay"
      onClick={props.shouldCloseOnClickAway ? props.onClose : undefined}
      transition={transition}
      withTint={show && (!!props.shouldCloseOnClickAway || !!props.withTint)}
    >
      {container}
    </Overlay>
  );

  return shouldRender
    ? (
      <BodyEnd>
        {props.withTint && withFocusTrap
          ? (
            <FocusTrap
              active
              focusTrapOptions={{
                tabbableOptions: {
                  displayCheck: 'legacy-full',
                },
              }}
            >
              <DrawerContainer className="drawer">
                {content}
              </DrawerContainer>
            </FocusTrap>
          )
          : (
            <div className="drawer">
              {content}
            </div>
          )}
      </BodyEnd>
    )
    : null;
};

export default Drawer;
